import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { adminOutreach, adminOutreachInvite, adminOutreachPreview, adminOutreachStatus, adminOutreachRunNudges } from "../lib/api";
import PitchSection from "./PitchSection";

const STATUS_LABEL = { not_contacted: "not contacted", contacted: "contacted", invited: "invited", declined: "declined", claimed: "claimed" };

function InviteForm({ t, onDone }) {
  const [email, setEmail] = useState(t.contact_email || "");
  const [variant, setVariant] = useState(t.views_30d > 0 || t.category_rank <= 10 ? "stats" : "plain");
  const [note, setNote] = useState("");
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const inp = "border border-black p-1 text-[12px] font-mono";

  const doPreview = async () => {
    try {
      setPreview(await adminOutreachPreview(t.slug, { variant, note: note || null }));
    } catch {
      toast.error("could not render preview");
    }
  };
  const send = async () => {
    if (!window.confirm(`send the ${variant} invite to ${email}?`)) return;
    setBusy(true);
    try {
      await adminOutreachInvite(t.slug, { email, variant, note: note || null });
      toast.success(`invite sent to ${email}`);
      onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "send failed");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="mt-2 border border-[#999] p-2 space-y-2" data-testid={`invite-form-${t.slug}`}>
      <div className="flex flex-wrap gap-2 items-center">
        <input placeholder="founder@company.com" value={email} onChange={(e) => setEmail(e.target.value)} className={`${inp} w-64`} data-testid={`invite-email-${t.slug}`} />
        <select value={variant} onChange={(e) => setVariant(e.target.value)} className={inp} data-testid={`invite-variant-${t.slug}`}>
          <option value="plain">plain note</option>
          <option value="stats">note + their stats</option>
        </select>
        <button type="button" className="tv-btn" onClick={doPreview} data-testid={`invite-preview-${t.slug}`}>[preview]</button>
        <button type="button" className="tv-btn tv-btn-primary" disabled={busy || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)} onClick={send} data-testid={`invite-send-${t.slug}`}>
          {busy ? "sending…" : "[send invite]"}
        </button>
      </div>
      <textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} maxLength={600}
        placeholder="optional personal opening line (replaces the default first sentence)" className={`${inp} w-full`} data-testid={`invite-note-${t.slug}`} />
      {preview && (
        <div data-testid={`invite-preview-body-${t.slug}`}>
          <div className="text-[#666]">subject: <b className="text-black">{preview.subject}</b></div>
          <pre className="whitespace-pre-wrap text-[11px] border border-[#ddd] p-2 mt-1 bg-[#fafafa]">{preview.text}</pre>
        </div>
      )}
    </div>
  );
}

function Row({ t, i, viewDays, onChanged }) {
  const [open, setOpen] = useState(false);
  const st = t.outreach?.status || "not_contacted";
  const mark = async (status) => {
    const note = status === "declined" ? window.prompt("reason (optional)") || null : null;
    try {
      await adminOutreachStatus(t.slug, { status, note });
      onChanged();
    } catch {
      toast.error("failed");
    }
  };
  return (
    <li className="border-b border-[#ddd] py-2" data-testid={`outreach-row-${t.slug}`}>
      <div className="flex flex-wrap gap-x-3 items-baseline">
        <span className="text-[#888] w-6">{i + 1}.</span>
        <a href={`/tool/${t.slug}`} className="tv-link font-bold" target="_blank" rel="noreferrer">{t.title}</a>
        <span className="text-[#666]">{t.views_30d} views/{viewDays}d · {t.gsc_clicks} gsc clicks · {t.gsc_impressions} impr · {t.upvotes} ▲
          {t.category_rank ? ` · #${t.category_rank}/${t.category_size} ${t.category}` : ""}</span>
        <span className={st === "not_contacted" ? "text-[#b91c1c]" : "font-bold"} data-testid={`outreach-status-${t.slug}`}>
          {STATUS_LABEL[st]}{t.outreach?.at ? ` ${String(t.outreach.at).slice(0, 10)}` : ""}{t.outreach?.email ? ` → ${t.outreach.email}` : ""}
        </span>
        <button type="button" className="tv-link underline" onClick={() => setOpen((o) => !o)} data-testid={`outreach-toggle-${t.slug}`}>{open ? "close" : "invite by email"}</button>
        {st !== "contacted" && <button type="button" className="tv-link underline text-[#666]" onClick={() => mark("contacted")} data-testid={`outreach-mark-contacted-${t.slug}`}>mark contacted</button>}
        {st !== "declined" && <button type="button" className="tv-link underline text-[#666]" onClick={() => mark("declined")} data-testid={`outreach-mark-declined-${t.slug}`}>declined</button>}
        {st !== "not_contacted" && <button type="button" className="tv-link underline text-[#666]" onClick={() => mark("not_contacted")}>reset</button>}
      </div>
      {t.invite && (
        <div className="text-[11px] text-[#666] pl-9">
          last invite → {t.invite.email} · sent {String(t.invite.created_at).slice(0, 10)} · {t.invite.variant}
          {" · "}<span data-testid={`invite-opened-${t.slug}`}>{t.invite.opened_at ? `opened ${String(t.invite.opened_at).slice(0, 10)}` : "not opened"}</span>
          {" · "}{t.invite.used_at ? <b className="text-black">claimed {String(t.invite.used_at).slice(0, 10)}</b> : `valid until ${String(t.invite.expires_at).slice(0, 10)}`}
          {t.invite.nudged_at && ` · nudged ${String(t.invite.nudged_at).slice(0, 10)}`}
          {t.invite.nudge_due && <b className="text-[#b91c1c]" data-testid={`invite-nudge-due-${t.slug}`}> · nudge due (sends automatically at 14:00 UTC)</b>}
        </div>
      )}
      {open && <InviteForm t={t} onDone={() => { setOpen(false); onChanged(); }} />}
    </li>
  );
}

export default function AdminOutreach() {
  const [d, setD] = useState(null);
  const [filter, setFilter] = useState("all");
  const load = useCallback(() => adminOutreach().then(setD).catch(() => toast.error("could not load outreach list")), []);
  useEffect(() => {
    Promise.resolve().then(load);
  }, [load]);
  if (!d) return <div className="text-[13px] text-[#888]">loading…</div>;
  const rows = d.tools.filter((t) => filter === "all" || (t.outreach?.status || "not_contacted") === filter);
  const runNudges = async () => {
    try {
      const r = await adminOutreachRunNudges();
      toast.success(`nudges: ${r.sent} sent, ${r.skipped} skipped, ${r.due} were due`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "nudge run failed");
    }
  };
  return (
    <div className="space-y-4 text-[12px]" data-testid="admin-outreach">
      <section>
        <h2 className="text-[15px] font-bold border-b border-black pb-1">vendor outreach — {d.tools.length} unclaimed tools, most-viewed first</h2>
        <p className="text-[#444] mt-2">
          Ranked by page views (last {d.view_days} days) + Google Search Console clicks/impressions + upvotes. Pitch Starter and sponsored slots to the top of this list first.
          Invites send a one-click claim link (valid 14 days, single use, no domain check) from <b>{d.email.from_name}</b>, replies go to <b>{d.email.reply_to}</b>.
          Link opens are recorded; one reminder goes out automatically {d.nudge_after_days} days after an invite that hasn't been claimed.
        </p>
        <p className="mt-1" data-testid="nudges-line">
          nudges due now: <b data-testid="nudges-due">{d.nudges_due}</b>{" "}
          <button type="button" className="tv-link underline" onClick={runNudges} data-testid="nudges-run">run reminders now</button>
        </p>
        <div className="flex flex-wrap gap-2 mt-3" data-testid="outreach-filters">
          {["all", ...Object.keys(STATUS_LABEL)].map((k) => (
            <button key={k} type="button" className={`tv-btn ${filter === k ? "tv-btn-primary" : ""}`} onClick={() => setFilter(k)} data-testid={`outreach-filter-${k}`}>
              [{k === "all" ? `all ${d.tools.length}` : `${STATUS_LABEL[k]} ${d.counts[k] || 0}`}]
            </button>
          ))}
        </div>
      </section>
      <ul data-testid="outreach-list">
        {rows.map((t, i) => <Row key={t.slug} t={t} i={i} viewDays={d.view_days} onChanged={load} />)}
        {rows.length === 0 && <li className="text-[#888]">nothing here.</li>}
      </ul>
      <PitchSection claimed={d.claimed} viewDays={d.view_days} onChanged={load} />
    </div>
  );
}
